import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Unit } from "../common/api";
import {
  CellCover,
  CELL_COVER_SIZE,
  clampLines,
  ProgressBar,
  ProgressBarFill,
} from "../common/CommonStyles";
import { t } from "i18next";
import {
  RECENTLY_ACTIVE_CELL_WIDTH,
  SURFACE_CARD_RADIUS_LARGE,
} from "../common/constants";
import { SPACE_MD, SPACE_LG } from "../common/spacing";
import { ELEVATION_1 } from "../common/elevation";
import {
  AnalyticsContext,
  RecentlyActiveUnitCellTapEvent,
} from "../common/context/AnalyticsContext";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_TERTIARY,
  COLOR_SURFACE_CONTAINER,
} from "../common/colors";

const RecentlyActiveUnitItemBody = styled.div`
  display: flex;
  width: ${RECENTLY_ACTIVE_CELL_WIDTH}px;
  border-radius: ${SURFACE_CARD_RADIUS_LARGE}px;
  background-color: ${COLOR_SURFACE_CONTAINER};
  box-shadow: ${ELEVATION_1};
  padding: ${SPACE_MD}px ${SPACE_LG}px ${SPACE_MD}px ${SPACE_MD}px;
`;

const Cover = styled(CellCover)`
  margin-right: ${SPACE_MD}px;
`;

const UnitDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: ${CELL_COVER_SIZE}px;
`;

const Title = styled.div`
  ${clampLines(2)}
  color: ${COLOR_TEXT_PRIMARY};
  font-weight: 700;
  font-size: 16px;
`;

const LessonsCompleted = styled.div`
  margin-top: auto;
  margin-bottom: 6px;
  color: ${COLOR_TEXT_TERTIARY};
  font-size: 10px;
`;

const StyledProgressBar = styled(ProgressBar)`
  width: 100%;
  height: 5px;
  border-radius: 5px;
`;

export const RecentlyActiveUnitItem = ({
  unit,
  completedLessonCount,
}: {
  unit: Unit;
  completedLessonCount: number;
}) => {
  const displayAsLesson = unit.display_as_lesson && unit.first_lesson != null;
  const { logEvent } = React.useContext(AnalyticsContext);

  return (
    <Link
      to={
        displayAsLesson
          ? `/lessons/${unit.first_lesson!.id}/${unit.first_lesson!.slug}/`
          : `/courses/${unit.id}/${unit.slug}/`
      }
      style={{ textDecoration: "none" }}
      onClick={() => {
        logEvent(new RecentlyActiveUnitCellTapEvent(unit.id, unit.title));
      }}
    >
      <RecentlyActiveUnitItemBody key={unit.id}>
        <Cover
          url={unit.cover_image}
          alt={t("unit_cover_content_description", { title: unit.title })}
          size={CELL_COVER_SIZE}
        />
        <UnitDetails>
          <Title>{unit.title}</Title>
          <LessonsCompleted>
            {t("unit_lessons_completed", {
              completed: completedLessonCount,
              total: unit.lesson_count,
            })}
          </LessonsCompleted>
          <StyledProgressBar>
            <ProgressBarFill
              width={Math.round(
                (completedLessonCount / unit.lesson_count) * 100
              )}
            />
          </StyledProgressBar>
        </UnitDetails>
      </RecentlyActiveUnitItemBody>
    </Link>
  );
};
